import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { getUserQuizStats, getAllCourseProgress } from '../utils/api';
import toast from '../utils/toastUtils';

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_QUIZ_STATS: 'SET_QUIZ_STATS',
  SET_COURSE_PROGRESS: 'SET_COURSE_PROGRESS',
  INVALIDATE_QUIZ_CACHE: 'INVALIDATE_QUIZ_CACHE',
  INVALIDATE_COURSE_CACHE: 'INVALIDATE_COURSE_CACHE',
  INVALIDATE_ALL: 'INVALIDATE_ALL',
};

// Initial state
const initialState = {
  quizStats: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
  courseProgress: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
};

// Reducer
const userDataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: action.loading,
          error: action.loading ? null : state[action.dataType].error,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: false,
          error: action.error,
        },
      };

    case ACTIONS.SET_QUIZ_STATS:
      return {
        ...state,
        quizStats: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.SET_COURSE_PROGRESS:
      return {
        ...state,
        courseProgress: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.INVALIDATE_QUIZ_CACHE:
      return {
        ...state,
        quizStats: {
          ...state.quizStats,
          isStale: true,
        },
      };

    case ACTIONS.INVALIDATE_COURSE_CACHE:
      return {
        ...state,
        courseProgress: {
          ...state.courseProgress,
          isStale: true,
        },
      };

    case ACTIONS.INVALIDATE_ALL:
      return {
        ...state,
        quizStats: { ...state.quizStats, isStale: true },
        courseProgress: { ...state.courseProgress, isStale: true },
      };

    default:
      return state;
  }
};

// Context
const UserDataContext = createContext();

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Provider component
export const UserDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userDataReducer, initialState);

  // Helper function to check if data is stale
  const isDataStale = useCallback((lastFetched) => {
    if (!lastFetched) return true;
    return Date.now() - lastFetched > CACHE_DURATION;
  }, []);

  // Fetch quiz stats with caching
  const fetchQuizStats = useCallback(async (forceRefresh = false) => {
    const { quizStats } = state;
    
    // Return cached data if fresh and not forcing refresh
    if (!forceRefresh && quizStats.data && !quizStats.isStale && !isDataStale(quizStats.lastFetched)) {
      return quizStats.data;
    }

    // Don't fetch if already loading
    if (quizStats.loading) return quizStats.data;

    dispatch({ type: ACTIONS.SET_LOADING, dataType: 'quizStats', loading: true });

    try {
      const data = await getUserQuizStats();
      dispatch({ type: ACTIONS.SET_QUIZ_STATS, data });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch quiz stats';
      dispatch({ type: ACTIONS.SET_ERROR, dataType: 'quizStats', error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state, isDataStale]);

  // Fetch course progress with caching
  const fetchCourseProgress = useCallback(async (forceRefresh = false) => {
    const { courseProgress } = state;
    
    // Return cached data if fresh and not forcing refresh
    if (!forceRefresh && courseProgress.data && !courseProgress.isStale && !isDataStale(courseProgress.lastFetched)) {
      return courseProgress.data;
    }

    // Don't fetch if already loading
    if (courseProgress.loading) return courseProgress.data;

    dispatch({ type: ACTIONS.SET_LOADING, dataType: 'courseProgress', loading: true });

    try {
      const data = await getAllCourseProgress();
      dispatch({ type: ACTIONS.SET_COURSE_PROGRESS, data });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch course progress';
      dispatch({ type: ACTIONS.SET_ERROR, dataType: 'courseProgress', error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state, isDataStale]);

  // Cache invalidation methods
  const invalidateQuizCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_QUIZ_CACHE });
  }, []);

  const invalidateCourseCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_COURSE_CACHE });
  }, []);

  const invalidateAllCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_ALL });
  }, []);

  // Method to call when quiz is completed
  const onQuizCompleted = useCallback(() => {
    invalidateQuizCache();
    // Also invalidate course progress as quiz completion might affect overall progress
    invalidateCourseCache();
  }, [invalidateQuizCache, invalidateCourseCache]);

  // Method to call when course module is completed
  const onCourseModuleCompleted = useCallback(() => {
    invalidateCourseCache();
  }, [invalidateCourseCache]);

  const value = {
    // State
    quizStats: state.quizStats,
    courseProgress: state.courseProgress,
    
    // Fetch methods
    fetchQuizStats,
    fetchCourseProgress,
    
    // Cache invalidation
    invalidateQuizCache,
    invalidateCourseCache,
    invalidateAllCache,
    
    // Event handlers for cache invalidation
    onQuizCompleted,
    onCourseModuleCompleted,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

// Hook to use the context
export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

export default UserDataContext; 
export { UserDataContext };